import { useState, useEffect } from 'react';
import api from '@/services/api';
import { useToast } from '@/hooks/useToast';
import ActionModal from './ActionModal';
import {
  CheckIcon,
  XMarkIcon,
  EyeIcon,
  ArrowPathIcon,
  DocumentArrowDownIcon,
  UserIcon,
  AcademicCapIcon,
  CurrencyDollarIcon,
  BellIcon,
} from '@heroicons/react/24/outline';
import styles from './NotificationPanel.module.css';

const NotificationPanel = ({ notifications, onClose, onReload }) => {
  const { addToast } = useToast();
  const [actionTarget, setActionTarget] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [filterType, setFilterType] = useState('todas');
  const [groupBy, setGroupBy] = useState(false);
  const [groups, setGroups] = useState([]);
  const [filteredNotifs, setFilteredNotifs] = useState(notifications);

  useEffect(() => {
    let filtered = notifications;
    if (filterType !== 'todas') {
      filtered = filtered.filter(n => n.tipo === filterType);
    }
    setFilteredNotifs(filtered);
  }, [filterType, notifications]);

  useEffect(() => {
    if (groupBy) {
      api.get('/notificaciones/agrupadas').then(res => setGroups(res.data));
    } else {
      setGroups([]);
    }
  }, [groupBy, notifications]);

  const handleAction = (notification, accion) => {
    if (accion === 'ver') {
      if (notification.tipo === 'emprendedor') {
        window.location.href = `/personas?detalle=${notification.metadata.id_persona}`;
      }
      onClose();
      return;
    }
    setActionTarget(notification);
    setActionType(accion);
  };

  const handleBatchAction = async (accion) => {
    if (selectedIds.length === 0) return;
    try {
      await api.post('/notificaciones/batch/atender', { ids: selectedIds });
      addToast(`${selectedIds.length} notificaciones atendidas`, 'success');
      onReload();
      setSelectedIds([]);
      setSelectAll(false);
    } catch (err) {
      addToast(err.message, 'error');
    }
  };

  const handleExport = async () => {
    try {
      const blob = await api.get('/notificaciones/exportar', { responseType: 'blob' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'notificaciones.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      addToast('Exportación completada', 'success');
    } catch (err) {
      addToast('Error al exportar', 'error');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredNotifs.map(n => n.id));
    }
    setSelectAll(!selectAll);
  };

  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Ahora mismo';
    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffHours < 24) return `Hace ${diffHours} h`;
    if (diffDays === 1) return 'Ayer';
    return date.toLocaleDateString();
  };

  const getIcon = (tipo) => {
    switch (tipo) {
      case 'usuario': return <UserIcon width={22} height={22} />;
      case 'emprendedor': return <UserIcon width={22} height={22} />;
      case 'capacitacion': return <AcademicCapIcon width={22} height={22} />;
      case 'apoyo': return <CurrencyDollarIcon width={22} height={22} />;
      default: return <BellIcon width={22} height={22} />;
    }
  };

  const handleConfirm = async (data) => {
    if (!actionTarget) return;
    const { tipo, metadata, id: notificacionId } = actionTarget;
    let resourceId = null;
    let endpoint = '';
    if (tipo === 'usuario') {
      resourceId = metadata.id_usuario;
      endpoint = `/usuarios/${resourceId}`;
    } else if (tipo === 'emprendedor') {
      resourceId = metadata.id_persona;
      endpoint = `/personas/${resourceId}`;
    } else {
      addToast('Tipo no soportado', 'error');
      return;
    }
    try {
      if (actionType === 'aprobar') {
        if (tipo === 'usuario') {
          await api.put(`${endpoint}/aprobar`, { rol: data.rol || 'operador' });
        } else {
          await api.put(`${endpoint}/aprobar`);
        }
        addToast('Aprobado correctamente', 'success');
      } else if (actionType === 'rechazar') {
        if (tipo === 'usuario') {
          await api.put(`${endpoint}/rechazar`, { motivo_rechazo: data.motivo });
        } else {
          await api.put(`${endpoint}/rechazar`, { motivo_rechazo: data.motivo });
        }
        addToast('Rechazado correctamente', 'warning');
      }
      await api.put(`/notificaciones/${notificacionId}/atender`);
      setActionTarget(null);
      setActionType(null);
      onReload();
    } catch (err) {
      if (err.status === 404) {
        // El registro referenciado ya no existe (fue eliminado) — no hay nada que
        // aprobar/rechazar, asi que solo se descarta la notificacion para que no
        // quede atascada.
        try {
          await api.put(`/notificaciones/${notificacionId}/atender`);
          addToast('Ese registro ya no existe, se descartó la notificación', 'warning');
          setActionTarget(null);
          setActionType(null);
          onReload();
          return;
        } catch (errAtender) {
          addToast(errAtender.message, 'error');
          return;
        }
      }
      addToast(err.message, 'error');
    }
  };

  return (
    <>
      <div className={styles.panel}>
        <div className={styles.header}>
          <h3>Notificaciones</h3>
          <div className={styles.headerActions}>
            <button className={styles.iconBtn} onClick={onReload} title="Refrescar">
              <ArrowPathIcon width={18} />
            </button>
            <button className={styles.iconBtn} onClick={handleExport} title="Exportar Excel">
              <DocumentArrowDownIcon width={18} />
            </button>
          </div>
        </div>

        <div className={styles.toolbar}>
          <label className={styles.checkboxLabel}>
            <input type="checkbox" checked={selectAll} onChange={toggleSelectAll} disabled={filteredNotifs.length === 0} />
            <span>Seleccionar todo</span>
          </label>
          <div className={styles.filters}>
            <select value={filterType} onChange={e => setFilterType(e.target.value)} className={styles.filterSelect}>
              <option value="todas">Todas</option>
              <option value="usuario">Usuarios</option>
              <option value="emprendedor">Emprendedores</option>
              <option value="capacitacion">Capacitaciones</option>
              <option value="apoyo">Apoyos</option>
            </select>
            <label className={styles.checkboxLabel}>
              <input type="checkbox" checked={groupBy} onChange={e => setGroupBy(e.target.checked)} />
              <span>Agrupar</span>
            </label>
          </div>
          {selectedIds.length > 0 && (
            <button className={styles.batchBtn} onClick={() => handleBatchAction('aprobar')}>
              Aprobar seleccionadas ({selectedIds.length})
            </button>
          )}
        </div>

        <div className={styles.list}>
          {filteredNotifs.length === 0 ? (
            <div className={styles.empty}>No hay notificaciones</div>
          ) : groupBy ? (
            groups.map(group => (
              <div key={group.tipo} className={styles.group}>
                <div className={styles.groupHeader}>
                  <span className={styles.groupIcon}>{getIcon(group.tipo)}</span>
                  <span className={styles.groupTitle}>
                    {group.tipo === 'usuario' ? 'Usuarios' : group.tipo === 'emprendedor' ? 'Emprendedores' : group.tipo}
                  </span>
                  <span className={styles.groupCount}>{group.cantidad}</span>
                </div>
              </div>
            ))
          ) : (
            filteredNotifs.map(notif => (
              <div key={notif.id} className={styles.item}>
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  checked={selectedIds.includes(notif.id)}
                  onChange={() => toggleSelect(notif.id)}
                />
                <div className={styles.avatar}>{getIcon(notif.tipo)}</div>
                <div
                  className={`${styles.content} ${notif.tipo === 'emprendedor' ? styles.clickable : ''}`}
                  onClick={() => notif.tipo === 'emprendedor' && handleAction(notif, 'ver')}
                >
                  <p className={styles.desc}>{notif.descripcion}</p>
                  <span className={styles.date}>{formatRelativeTime(notif.created_at)}</span>
                </div>
                <div className={styles.actions}>
                  {notif.acciones.includes('ver') && (
                    <button className={styles.actionBtn} onClick={() => handleAction(notif, 'ver')} title="Ver">
                      <EyeIcon width={16} />
                    </button>
                  )}
                  {notif.acciones.includes('aprobar') && (
                    <button className={`${styles.actionBtn} ${styles.approve}`} onClick={() => handleAction(notif, 'aprobar')}>
                      <CheckIcon width={16} />
                    </button>
                  )}
                  {notif.acciones.includes('rechazar') && (
                    <button className={`${styles.actionBtn} ${styles.reject}`} onClick={() => handleAction(notif, 'rechazar')}>
                      <XMarkIcon width={16} />
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {actionTarget && actionType && (
        <ActionModal
          isOpen={!!actionTarget}
          onClose={() => { setActionTarget(null); setActionType(null); }}
          title={`${actionType === 'aprobar' ? 'Aprobar' : 'Rechazar'} ${actionTarget.tipo === 'usuario' ? 'usuario' : 'emprendedor'}`}
          type={actionType}
          metadata={actionTarget.metadata}
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
};

export default NotificationPanel;
